import { useState, type ReactNode } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { InfoCallout } from '../../components/documentation';
import styles from '../../components/documentation/DocContent.module.css';
import guideStyles from './StreamingGuide.module.css';
import createProjectShot from '../../assets/docs/unreal/create-project.png';
import createApplicationShot from '../../assets/docs/unreal/create-application.png';
import addVersionShot from '../../assets/docs/unreal/add-version.png';

export const Route = createFileRoute('/documentation/streaming-guide-unreal')({
  component: StreamingGuideUnrealPage,
});

const CHECKLIST: { id: string; title: string; items: ReactNode[]; note?: ReactNode }[] = [
  {
    id: 'checklist-engine',
    title: 'Engine configuration',
    items: [
      'Pixel Streaming (1) is enabled and Pixel Streaming 2 is not.',
      'The project opens and runs in the editor.',
      'All required content is included in the project.',
    ],
  },
  {
    id: 'checklist-packaging',
    title: 'Packaging',
    items: [
      'Packaged for Windows 64-bit, with no errors.',
      <>The <code>.exe</code> and all supporting folders are present.</>,
      'The correct startup map is configured and included in the build.',
    ],
  },
  {
    id: 'checklist-runtime',
    title: 'The packaged build runs',
    items: [
      <>The <code>.exe</code> launches on a Windows machine and loads the expected level.</>,
      'No missing asset, plugin, or dependency errors.',
      'Keyboard and mouse input work.',
      'It renders without crashes or obvious graphical faults.',
    ],
    note: (
      <>
        Running the build locally confirms the application works. It does not
        test streaming. You will see that once the version is live on
        Interlucent.
      </>
    ),
  },
  {
    id: 'checklist-zip',
    title: 'The zip',
    items: [
      <>Contains the whole <code>Windows</code> folder with its structure intact.</>,
      'Opens without errors.',
      'Holds no intermediate build files or unrelated project files.',
      'Comes from the latest intended build and has a recognisable filename.',
      'Contains no passwords, API keys, credentials, or other sensitive files.',
    ],
  },
];

const CHECKLIST_SIZE = CHECKLIST.reduce((n, g) => n + g.items.length, 0);

function StreamingGuideUnrealPage() {
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const toggle = (key: string) =>
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className={guideStyles.page}>
      <div className={styles.breadcrumb}>Engine Guides</div>
      <h1 className={styles.pageTitle}>Streaming Guide (Unreal Engine)</h1>
      <p className={styles.pageSubtitle}>
        Take an Unreal Engine project from the editor to a live Interlucent
        application: enable Pixel Streaming, package a standalone Windows
        build, zip it, and upload it from the console. Interlucent runs the
        signalling server and launches your application with the right
        command-line parameters, so none of that is covered here.
      </p>

      {/* ── Before You Begin ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="prerequisites">
          Before You Begin
        </h2>
        <p className={styles.prose}>You will need:</p>
        <ul className={styles.featureList}>
          <li className={styles.featureItem}>
            Unreal Engine with the Pixel Streaming plugin available.
          </li>
          <li className={styles.featureItem}>
            A project that opens and runs in the editor.
          </li>
          <li className={styles.featureItem}>
            A Windows machine set up for packaging, with the required
            development tools installed.
          </li>
          <li className={styles.featureItem}>
            Enough free disk space for the packaged build and its zip.
          </li>
          <li className={styles.featureItem}>
            A console account in the organization you will stream from.
          </li>
        </ul>
      </section>

      {/* ── Step 1: Enable Pixel Streaming ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="enable-pixel-streaming">
          1 — Enable Pixel Streaming
        </h2>
        <ol className={styles.stepList}>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>1</span>
            <span>Open your project in the Unreal Editor.</span>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>2</span>
            <span>
              Go to <strong>Edit → Plugins</strong> and search
              for <strong>Pixel Streaming</strong>.
            </span>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>3</span>
            <span>
              Tick the original <strong>Pixel Streaming</strong> plugin
              (Pixel Streaming 1).
            </span>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>4</span>
            <span>
              Click <strong>Restart Now</strong> when prompted, and check the
              project loads cleanly after the restart.
            </span>
          </li>
        </ol>
        <InfoCallout variant="warning">
          Enable <strong>Pixel Streaming</strong>, not{' '}
          <strong>Pixel Streaming 2</strong>, even on engine versions that
          offer both. This guide targets the original plugin; leave Pixel
          Streaming 2 disabled.
        </InfoCallout>
      </section>

      {/* ── Step 2: Touch Controls ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="touch-controls">
          2 — Touch Controls (Optional)
        </h2>
        <p className={styles.prose}>
          If your users will be on phones or tablets and your application needs
          an on-screen touch interface, open <strong>Edit → Project
          Settings</strong>, go to <strong>Engine → Input</strong>, and
          enable <strong>Always Show Touch Interface</strong>. Otherwise, skip
          this step.
        </p>
      </section>

      {/* ── Step 3: Package for Windows ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="package">
          3 — Package for Windows
        </h2>
        <ol className={styles.stepList}>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>1</span>
            <span>
              Save all changes, and make sure every asset the application needs
              is part of the project.
            </span>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>2</span>
            <span>
              From the editor toolbar, choose <strong>Platforms → Windows →
              Package Project</strong>.
            </span>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>3</span>
            <span>
              Pick an output folder with plenty of free space and
              click <strong>Select Folder</strong>.
            </span>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>4</span>
            <span>
              Wait for packaging to finish. Large projects can take several
              minutes. If it fails, the Output Log shows the reason.
            </span>
          </li>
        </ol>
        <p className={styles.prose}>
          The output folder now contains a <code className={styles.mono}>Windows</code> folder
          holding your executable (<code className={styles.mono}>.exe</code>),
          its libraries and dependencies, the cooked content, and supporting
          files. The executable needs all of them to run, so keep the folder
          intact.
        </p>
      </section>

      {/* ── Step 4: Zip the Build ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="zip">
          4 — Zip the Build
        </h2>
        <p className={styles.prose}>
          Right-click the <code className={styles.mono}>Windows</code> folder
          and choose <strong>Compress to ZIP file</strong> (Windows 11)
          or <strong>Send to → Compressed (zipped) folder</strong> (Windows
          10). Open the finished archive and check that every subfolder and
          supporting file is inside.
        </p>
        <InfoCallout variant="warning">
          Zip the whole <code className={styles.mono}>Windows</code> folder, not
          just the <code className={styles.mono}>.exe</code>. An archive that
          holds only the executable will not run.
        </InfoCallout>
      </section>

      {/* ── Pre-Upload Checklist ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="checklist">
          Pre-Upload Checklist
        </h2>
        <p className={styles.prose}>
          Tick off each item before uploading. Most failed deployments trace
          back to one of them.
        </p>
        <p className={styles.checklistProgress} aria-live="polite">
          {checked.size} of {CHECKLIST_SIZE} checked
        </p>
        {CHECKLIST.map(group => (
          <div key={group.id}>
            <h3 className={styles.subsectionTitle} id={group.id}>
              {group.title}
            </h3>
            <ul className={styles.checklist}>
              {group.items.map((item, i) => {
                const key = `${group.id}-${i}`;
                return (
                  <li key={key}>
                    <label className={styles.checklistItem}>
                      <input
                        type="checkbox"
                        checked={checked.has(key)}
                        onChange={() => toggle(key)}
                      />
                      <span>{item}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
            {group.note && (
              <InfoCallout variant="info">{group.note}</InfoCallout>
            )}
          </div>
        ))}
      </section>

      {/* ── Step 5: Upload from the Console ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="upload">
          5 — Upload from the Console
        </h2>
        <p className={styles.prose}>
          In the console, a <strong>project</strong> groups your applications,
          an <strong>application</strong> is one streamable experience, and each
          zip you upload becomes a <strong>version</strong> of that
          application.
        </p>

        <h3 className={styles.subsectionTitle} id="upload-create-project">
          Create a project
        </h3>
        <ol className={styles.stepList}>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>1</span>
            <span>
              Sign in to the console and open <strong>Projects</strong>.
            </span>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>2</span>
            <span>
              Click <strong>Create Project</strong>, enter a project name, and
              click <strong>Create Project</strong>.
            </span>
          </li>
        </ol>
        <figure className={styles.figure}>
          <img
            src={createProjectShot}
            alt="The Create New Project dialog with a Project Name field and a Create Project button"
          />
        </figure>
        <p className={styles.prose}>
          If your organization already has a project for this work, skip
          ahead and open it.
        </p>

        <h3 className={styles.subsectionTitle} id="upload-create-application">
          Create an application
        </h3>
        <ol className={styles.stepList}>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>1</span>
            <span>
              Open the project and click <strong>Create Application</strong>.
            </span>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>2</span>
            <span>
              Enter an <strong>Application Name</strong>.
            </span>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>3</span>
            <span>
              Under <strong>Build Archive</strong>, select the zip from step 4.
              The console reads it and fills in the engine, operating system,
              and executable for you. Look for the <strong>PixelStreaming</strong> tag
              under the archive name. If the console reports no Pixel Streaming
              plugin, go back to step 1.
            </span>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>4</span>
            <span>
              Check the filled-in fields: <strong>Game Engine</strong> should
              be <strong>Unreal Engine</strong>, <strong>Operating
              System</strong> should be <strong>Windows</strong>,
              and <strong>Executable Path</strong> should point to your
              project's <code className={styles.mono}>.exe</code>, usually the
              one at the top of the build. Click the field to pick a different
              file from the zip.
            </span>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>5</span>
            <span>
              Leave <strong>Command Arguments</strong> and{' '}
              <strong>Environment Variables</strong> empty unless your
              application needs them.
            </span>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>6</span>
            <span>
              Click <strong>Upload Build</strong>. Progress appears in the
              project's application list. Keep the tab open until the upload
              finishes.
            </span>
          </li>
        </ol>
        <figure className={styles.figure}>
          <img
            src={createApplicationShot}
            alt="The Create Application dialog after selecting a build archive: OS, engine, executable and PixelStreaming detected, with Game Engine, Operating System and Executable Path filled in"
          />
          <figcaption className={styles.figureCaption}>
            After you select the zip, the detected OS, engine, executable, and
            Pixel Streaming plugin appear under the archive name.
          </figcaption>
        </figure>

        <h3 className={styles.subsectionTitle} id="upload-new-version">
          Upload a new build later
        </h3>
        <p className={styles.prose}>
          To ship an updated build of the same application, package and zip it
          as above, open the application, click <strong>Add Version</strong>,
          and select the new zip. The other fields work the same way as when
          you created the application.
        </p>
        <figure className={styles.figure}>
          <img
            src={addVersionShot}
            alt="The Add Version dialog for an existing application, with the build archive analyzed and fields filled in"
          />
        </figure>
        <InfoCallout variant="tip">
          Publishing builds from a continuous integration pipeline? Use
          the <Link to="/documentation/interlucent-uploader">Interlucent
          Uploader</Link>, a CLI made for CI pipelines, instead of the console.
        </InfoCallout>
      </section>

      {/* ── Next Steps ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="next-steps">
          Next Steps
        </h2>
        <p className={styles.prose}>
          Once the version is live, follow
          the <Link to="/documentation/streaming-guide">Streaming Guide</Link> to
          embed it in your page.
        </p>
        <p className={styles.prose}>
          Adapted from Epic Games'{' '}
          <a
            href="https://dev.epicgames.com/documentation/unreal-engine/getting-started-with-pixel-streaming-in-unreal-engine"
            target="_blank"
            rel="noopener noreferrer"
          >
            Getting Started with Pixel Streaming in Unreal Engine
          </a>
          .
        </p>
      </section>
    </div>
  );
}
